import { fetchIssues, fetchRepos, fetchStats } from "@/api/repos";
import { ContributeCTA } from "@/components/ContributeCTA";
import { ContributionGraph } from "@/components/ContributionGraph";
import { FilterBar } from "@/components/FilterBar";
import { GoodFirstIssues } from "@/components/GoodFirstIssues";
import { LatestReleases } from "@/components/LatestReleases";
import { RepoCard } from "@/components/RepoCard";
import { SkeletonCard } from "@/components/SkeletonCard";
import { StatsBanner } from "@/components/StatsBanner";
import { Carousel } from "@/components/ui/Carousel";
import type { Issue, Repo, Stats } from "@/types/repo";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";
import { motion } from "framer-motion";
import { useEffect, useMemo, useState } from "react";

const PER_PAGE = 9;

export function Showcase() {
  const [repos, setRepos] = useState<Repo[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [issues, setIssues] = useState<Issue[]>([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [language, setLang] = useState("");
  const [topic, setTopic] = useState("");
  const [page, setPage] = useState(1);

  useEffect(() => {
    Promise.all([fetchRepos(), fetchStats(), fetchIssues()])
      .then(([r, s, i]) => {
        setRepos(r);
        setStats(s);
        setIssues(i);
      })
      .finally(() => setLoading(false));
  }, []);

  // Derived filter values
  const languages = useMemo(
    () => [...new Set(repos.map((r) => r.language).filter(Boolean))],
    [repos],
  );
  const allTopics = useMemo(
    () => [...new Set(repos.flatMap((r) => r.topics))].slice(0, 12),
    [repos],
  );

  // Filtered repos
  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return repos.filter((r) => {
      if (
        q &&
        !r.name.toLowerCase().includes(q) &&
        !r.description.toLowerCase().includes(q)
      )
        return false;
      if (language && r.language !== language) return false;
      if (topic && !r.topics.includes(topic)) return false;
      return true;
    });
  }, [repos, search, language, topic]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const paginated = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  // Reset page on filter change
  useEffect(() => {
    setPage(1);
  }, [search, language, topic]);

  return (
    <main id="main" tabIndex={-1}>
      {/* Hero */}
      <section className="section-container pt-16 pb-10 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <span className="inline-block text-xs font-semibold px-3 py-1 rounded-full bg-primary-muted text-primary mb-4 uppercase tracking-widest">
            Open Source
          </span>
          <h1 className="text-4xl sm:text-5xl font-brand font-black text-text-primary mb-4">
            Open Source of <span className="text-gradient">KuberCoin</span>
          </h1>
          <p className="text-lg text-text-secondary max-w-2xl mx-auto">
            Explore our repositories, contribute code, report bugs, and help
            build the future of peer-to-peer digital money.
          </p>
        </motion.div>
      </section>

      {/* Feature Carousel */}
      <section className="section-container pb-10">
        <Carousel autoAdvance={5000}>
          <div className="flex flex-col items-center justify-center h-40 rounded-xl bg-primary/5 border border-primary/20 p-6 text-center">
            <h3 className="text-lg font-bold text-text-primary">
              Lightning Network
            </h3>
            <p className="text-sm text-text-muted mt-1">
              Instant off-chain transactions with near-zero fees
            </p>
          </div>
          <div className="flex flex-col items-center justify-center h-40 rounded-xl bg-green-500/5 border border-green-500/20 p-6 text-center">
            <h3 className="text-lg font-bold text-text-primary">
              Zero-Knowledge Proofs
            </h3>
            <p className="text-sm text-text-muted mt-1">
              Privacy-preserving transactions powered by zkSNARKs
            </p>
          </div>
          <div className="flex flex-col items-center justify-center h-40 rounded-xl bg-amber-500/5 border border-amber-500/20 p-6 text-center">
            <h3 className="text-lg font-bold text-text-primary">
              Native Transaction Signing
            </h3>
            <p className="text-sm text-text-muted mt-1">
              Local UTXO signing that keeps private keys off the network
            </p>
          </div>
        </Carousel>
      </section>

      {/* Stats */}
      <section className="section-container pb-12">
        <StatsBanner stats={stats} />
      </section>

      {/* Repo grid */}
      <section className="section-container pb-16">
        <h2 className="text-2xl font-bold text-text-primary mb-5">
          Repositories
        </h2>

        <FilterBar
          languages={languages}
          topics={allTopics}
          onSearch={setSearch}
          onLanguage={(l) => setLang(l)}
          onTopic={(t) => setTopic(t)}
          search={search}
          language={language}
          topic={topic}
        />

        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {loading
            ? Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)
            : paginated.map((repo) => (
                <motion.div
                  key={repo.id}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <RepoCard repo={repo} />
                </motion.div>
              ))}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-3 mt-8">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="flex items-center gap-1 px-4 py-2 rounded-lg border border-border text-sm text-text-secondary disabled:opacity-40 hover:border-primary hover:text-primary-light transition-all"
            >
              <CaretLeft size={14} /> Prev
            </button>
            <span className="text-sm text-text-muted">
              {page} / {totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="flex items-center gap-1 px-4 py-2 rounded-lg border border-border text-sm text-text-secondary disabled:opacity-40 hover:border-primary hover:text-primary-light transition-all"
            >
              Next <CaretRight size={14} />
            </button>
          </div>
        )}
      </section>

      {/* Contribution heatmap */}
      <section className="section-container pb-16">
        <h2 className="text-2xl font-bold text-text-primary mb-5">Activity</h2>
        <div className="eth-card p-6">
          <ContributionGraph repos={repos} />
        </div>
      </section>

      {/* Latest releases */}
      <section className="section-container pb-16">
        <h2 className="text-2xl font-bold text-text-primary mb-5">
          Latest Releases
        </h2>
        <LatestReleases repos={repos} />
      </section>

      {/* Good first issues */}
      <section className="section-container pb-16">
        <h2 className="text-2xl font-bold text-text-primary mb-5">
          Good First Issues
        </h2>
        <GoodFirstIssues issues={issues} />
      </section>

      {/* CTA */}
      <section className="section-container pb-20">
        <ContributeCTA />
      </section>
    </main>
  );
}
